// src/modules/dl/NeuralNetworks.jsx
// Deep Learning → Neural Networks
// Tabs: overview · architecture · forward · train · playground · compare

import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

// ─── PALETTE ──────────────────────────────────────────────────
const AC = 'var(--cyan)';
const P  = ['var(--cyan)','var(--green)','var(--violet)','var(--gold)','var(--red)','var(--orange)'];

// ─── MINI UI ──────────────────────────────────────────────────
const Btn = ({ onClick, children, color = AC, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding: '9px 18px', background: color+'18', border: `1px solid ${color}`,
    color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer',
    borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700,
    letterSpacing: '.08em', opacity: disabled ? 0.4 : 1, transition: 'all .12s', ...style,
  }}>{children}</button>
);
const Sl = ({ label, value, min, max, step = 0.01, onChange, unit = '', color = AC }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
      <span style={{ fontSize: '10px', color: 'var(--t2)' }}>{label}</span>
      <span style={{ fontSize: '10px', color, fontWeight: 700 }}>{typeof value === 'number' ? value.toFixed(step < 0.01 ? 4 : step < 1 ? 2 : 0) : value}{unit}</span>
    </div>
    <div style={{ position: 'relative', height: '3px', background: 'var(--b0)', borderRadius: '2px' }}>
      <div style={{ position: 'absolute', left: 0, height: '100%', borderRadius: '2px', background: `linear-gradient(90deg,${color}44,${color})`, width: `${((value-min)/(max-min))*100}%` }} />
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(parseFloat(e.target.value))}
        style={{ position: 'absolute', inset: '-7px 0', width: '100%', opacity: 0, cursor: 'pointer', height: '18px' }} />
    </div>
  </div>
);
const Sel = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: '10px' }}>
    {label && <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em', marginBottom: '4px' }}>{label}</div>}
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)',
      color: 'var(--t1)', padding: '7px 8px', fontSize: '11px',
      fontFamily: 'JetBrains Mono', borderRadius: '2px', outline: 'none',
    }}>
      {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  </div>
);

const TABS = ['overview', 'architecture', 'forward', 'train', 'playground', 'compare'];

// ═══════════════════════════════════════════════════════════════
// MATH ENGINE
// ═══════════════════════════════════════════════════════════════
const randn = () => {
  let u=0,v=0;
  while(!u) u=Math.random();
  while(!v) v=Math.random();
  return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v);
};

const ACTS = {
  relu:       { fn: x => Math.max(0,x),                       d: x => x>0?1:0,                         color: P[0] },
  sigmoid:    { fn: x => 1/(1+Math.exp(-x)),                  d: x => { const s=1/(1+Math.exp(-x)); return s*(1-s); }, color: P[2] },
  tanh:       { fn: x => Math.tanh(x),                        d: x => 1-Math.tanh(x)**2,               color: P[1] },
  leaky_relu: { fn: x => x>0?x:0.01*x,                        d: x => x>0?1:0.01,                      color: P[3] },
  swish:      { fn: x => x/(1+Math.exp(-x)),                  d: x => { const s=1/(1+Math.exp(-x)); return s+x*s*(1-s); }, color: P[5] },
};

const INITS = {
  xavier: (fi,fo) => randn()*Math.sqrt(2/(fi+fo)),
  he:     (fi)    => randn()*Math.sqrt(2/fi),
  lecun:  (fi)    => randn()*Math.sqrt(1/fi),
  random: ()      => randn()*0.5,
  zeros:  ()      => 0,
};

const initWeights = (layers, initType='xavier') => {
  const W=[],B=[];
  for(let i=0;i<layers.length-1;i++){
    const fi=layers[i],fo=layers[i+1];
    W.push(Array.from({length:fo},()=>Array.from({length:fi},()=>INITS[initType](fi,fo))));
    B.push(Array(fo).fill(0));
  }
  return {W,B};
};

const forward = (x,W,B,actName='relu',outputAct='sigmoid') => {
  const act=ACTS[actName]?.fn ?? (v=>Math.max(0,v));
  const outAct=ACTS[outputAct]?.fn ?? (v=>1/(1+Math.exp(-v)));
  const zs=[],as=[x];
  let cur=x;
  W.forEach((Wl,l)=>{
    const z=Wl.map((row,i)=>row.reduce((s,w,j)=>s+w*cur[j],0)+B[l][i]);
    zs.push(z);
    cur=l===W.length-1?z.map(outAct):z.map(act);
    as.push(cur);
  });
  return {as,zs};
};

const backprop = (x,y,W,B,actName,lr) => {
  const act=ACTS[actName];
  const outFn=v=>1/(1+Math.exp(-v));
  const outD=v=>{ const s=outFn(v); return s*(1-s); };
  const {as,zs}=forward(x,W,B,actName,'sigmoid');
  const pred=as[as.length-1];
  const loss=pred.reduce((s,p,i)=>s+(p-y[i])**2,0)/pred.length;
  let delta=pred.map((p,i)=>2*(p-y[i])/pred.length*outD(zs[zs.length-1][i]));
  const nW=W.map(l=>l.map(r=>[...r]));
  const nB=B.map(b=>[...b]);
  for(let l=W.length-1;l>=0;l--){
    const pA=as[l];
    nW[l]=nW[l].map((row,i)=>row.map((w,j)=>w-lr*delta[i]*pA[j]));
    nB[l]=nB[l].map((b,i)=>b-lr*delta[i]);
    if(l>0){
      delta=pA.map((_,j)=>nW[l].reduce((s,row,i)=>s+row[j]*delta[i],0)*act.d(zs[l-1][j]));
    }
  }
  return {W:nW,B:nB,loss,pred};
};

const DATASETS = {
  xor: {
    name:'XOR', color:'var(--cyan)',
    inputs:[[0,0],[0,1],[1,0],[1,1]], targets:[[0],[1],[1],[0]],
    desc:'Non-linearly separable. Needs ≥1 hidden layer.',
  },
  circle: {
    name:'Circles', color:'var(--violet)',
    inputs: Array.from({length:60},()=>{ const a=Math.random()*2*Math.PI,r=Math.random()>0.5?(0.7+Math.random()*0.2):(0.1+Math.random()*0.2); return [r*Math.cos(a),r*Math.sin(a)]; }),
    targets: null, // built below
    desc:'Concentric circles — radial decision boundary.',
  },
  spiral: {
    name:'Spiral', color:'var(--orange)',
    inputs: Array.from({length:60},(_,i)=>{ const t=(i%30)/30*4,r=t/4,n=randn()*0.05; return i<30?[r*Math.cos(t)+n,r*Math.sin(t)+n]:[r*Math.cos(t+Math.PI)+n,r*Math.sin(t+Math.PI)+n]; }),
    targets: Array.from({length:60},(_,i)=>i<30?[0]:[1]),
    desc:'Interleaved spirals — very hard. Deep nets needed.',
  },
};
// Build circle targets based on radius
DATASETS.circle.targets = DATASETS.circle.inputs.map(([x,y])=>[Math.sqrt(x*x+y*y)>0.45?1:0]);

const decisionGrid = (W,B,actName,res=22) => {
  const grid=[];
  for(let xi=0;xi<res;xi++) for(let yi=0;yi<res;yi++){
    const x=-1.2+(xi/(res-1))*2.4, y=-1.2+(yi/(res-1))*2.4;
    const {as}=forward([x,y],W,B,actName,'sigmoid');
    grid.push({x:+x.toFixed(2),y:+y.toFixed(2),p:+as[as.length-1][0].toFixed(3)});
  }
  return grid;
};

// ═══════════════════════════════════════════════════════════════
// SHARED: LIVE NETWORK DIAGRAM
// Shows the network with neuron activations highlighted
// activations: array of arrays (as from forward pass), or null for blank
// highlightLayer: which layer is "active" (pulse ring)
// ═══════════════════════════════════════════════════════════════
function NetworkDiagram({ layers, activations, highlightEdges, compact=false }) {
  const W_svg = compact ? 340 : 520;
  const H_svg = compact ? 220 : 300;
  const maxDraw = compact ? 6 : 8;

  const nL = layers.length;
  const lGap = W_svg / (nL + 1);
  const LAYER_COLORS = ['var(--cyan)', ...Array(Math.max(0,nL-2)).fill('var(--violet)'), 'var(--green)'];

  const positions = layers.map((n, li) => {
    const x = lGap * (li + 1);
    const drawN = Math.min(n, maxDraw);
    const nGap = H_svg / (drawN + 1);
    return Array.from({length: drawN}, (_, ni) => ({ x, y: nGap*(ni+1), li, ni, total: n }));
  });

  const getActVal = (li, ni) => {
    if (!activations || !activations[li]) return null;
    const v = activations[li][ni];
    return v !== undefined ? v : null;
  };

  const allActsFlat = activations ? activations.flat().filter(v=>v!==undefined) : [];
  const maxAbs = allActsFlat.length ? Math.max(...allActsFlat.map(Math.abs), 0.001) : 1;

  return (
    <svg width="100%" height="320" viewBox={`0 0 ${W_svg} ${H_svg+32}`} style={{ display: 'block' }}>
      {/* Edges */}
      {positions.slice(0,-1).map((layer, li) =>
        layer.slice(0, compact?4:6).flatMap(from =>
          positions[li+1].slice(0, compact?4:6).map(to => {
            // Color edge by source activation if available
            const av = getActVal(li, from.ni);
            const opacity = av !== null ? 0.05 + Math.abs(av)/maxAbs * 0.95 : 1;
            return (
              <line key={`e-${li}-${from.ni}-${to.ni}`}
                x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke={av !== null && av > 0 ? 'var(--cyan)' : av !== null && av < 0 ? 'var(--red)' : 'var(--b2)'}
                strokeWidth="0.8" opacity={opacity} />
            );
          })
        )
      )}

      {/* Nodes */}
      {positions.map((layer, li) =>
        layer.map(({ x, y, ni, total }) => {
          const col = LAYER_COLORS[Math.min(li, LAYER_COLORS.length-1)];
          const av = getActVal(li, ni);
          const norm = av !== null ? Math.abs(av) / maxAbs : 0;
          const isOverflow = ni === layer.length-1 && total > maxDraw;
          const isDead = av !== null && av === 0; // dead relu neuron

          if (isOverflow) return (
            <g key={`n-${li}-${ni}`}>
              <text x={x} y={y+2} textAnchor="middle" fill={col} fontSize={16} opacity={0.4}>⋮</text>
              <text x={x} y={y+16} textAnchor="middle" fill="var(--t3)" fontSize={8} fontFamily="JetBrains Mono">+{total-layer.length+1}</text>
            </g>
          );

          return (
            <g key={`n-${li}-${ni}`}>
              {/* Activation glow ring */}
              {av !== null && norm > 0.1 && (
                <circle cx={x} cy={y} r={12+norm*6} fill="none"
                  stroke={av > 0 ? col : 'var(--red)'}
                  strokeWidth={1} opacity={norm*0.5} />
              )}
              {/* Main node */}
              <circle cx={x} cy={y} r={10}
                fill={av !== null
                  ? (isDead ? 'var(--b1)' : `${av>0?col:'var(--red)'}${Math.round(20+norm*200).toString(16).padStart(2,'0')}`)
                  : col+'15'}
                stroke={isDead ? 'var(--b1)' : col}
                strokeWidth={av !== null ? 1.5+norm*1.5 : 1} />
              {/* Value label */}
              {av !== null && (
                <text x={x} y={y+3} textAnchor="middle"
                  fill={norm > 0.5 ? 'var(--void)' : col}
                  fontSize={compact?7:7.5} fontFamily="JetBrains Mono" fontWeight="600">
                  {isDead ? '0' : av > 0 ? av.toFixed(1) : av.toFixed(1)}
                </text>
              )}
            </g>
          );
        })
      )}

      {/* Layer labels + neuron counts */}
      {layers.map((n, li) => {
        const x = lGap*(li+1);
        const col = LAYER_COLORS[Math.min(li, LAYER_COLORS.length-1)];
        return (
          <g key={`lbl-${li}`}>
            <text x={x} y={H_svg+14} textAnchor="middle" fill="var(--t3)" fontSize={8} fontFamily="JetBrains Mono">
              {li===0?'INPUT':li===nL-1?'OUTPUT':`H${li}`}
            </text>
            <text x={x} y={H_svg+26} textAnchor="middle" fill={col} fontSize={9} fontFamily="JetBrains Mono" fontWeight="bold">
              {n}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: OVERVIEW
// Timeline + param scale + neuron anatomy + concept table (no 6-card grid)
// ═══════════════════════════════════════════════════════════════
function TabOverview() {
  const timeline = [
    { year:'1943', event:'McCulloch-Pitts neuron — first formal model', color:'var(--t3)' },
    { year:'1957', event:'Perceptron (Rosenblatt) — first trainable model', color:'var(--t3)' },
    { year:'1969', event:'Minsky & Papert — XOR kills perceptron hype (first AI winter)', color:'var(--red)' },
    { year:'1986', event:'Backpropagation rediscovered — Rumelhart, Hinton, Williams', color:'var(--gold)' },
    { year:'1998', event:'LeNet-5 — first practical deep CNN (LeCun) — MNIST', color:'var(--cyan)' },
    { year:'2006', event:'Deep Belief Nets + pre-training (Hinton) — DL revival', color:'var(--cyan)' },
    { year:'2012', event:'AlexNet wins ImageNet by huge margin — GPU + ReLU + Dropout', color:'var(--green)' },
    { year:'2015', event:'ResNet — 152 layers, skip connections — vanishing grad solved', color:'var(--green)' },
    { year:'2017', event:'"Attention Is All You Need" — Transformer architecture born', color:'var(--violet)' },
    { year:'2020', event:'GPT-3 — 175B params, few-shot, emergent capabilities', color:'var(--violet)' },
    { year:'2022+', event:'LLMs, Diffusion, multimodal — DL becomes the infrastructure of AI', color:'var(--orange)' },
  ];

  const paramScale = [
    { model:'Perceptron',  params:10,            color:'var(--t3)' },
    { model:'LeNet-5',     params:60000,          color:'var(--cyan)' },
    { model:'AlexNet',     params:61000000,       color:'var(--green)' },
    { model:'ResNet-50',   params:25000000,       color:'var(--gold)' },
    { model:'BERT-Large',  params:340000000,      color:'var(--violet)' },
    { model:'GPT-3',       params:175000000000,   color:'var(--orange)' },
  ];

  const conceptTable = [
    { concept:'Universal Approx. Theorem', what:'1 hidden layer + enough neurons = any continuous function on compact domain', why:'Theoretical proof that NNs are expressive enough', color:'var(--cyan)' },
    { concept:'Depth vs Width',            what:'Deep narrow >> shallow wide for same param count. Each layer squares expressivity.', why:'Why we go deep, not just wide', color:'var(--green)' },
    { concept:'Non-linearity is mandatory', what:'Without activation functions, stacking layers = still just one linear transform', why:'Why we need ReLU/sigmoid/tanh', color:'var(--violet)' },
    { concept:'Inductive Bias',            what:'Architecture choice = assumptions about data structure. MLP assumes none. CNN assumes locality.', why:'Right arch for right problem = faster convergence', color:'var(--gold)' },
    { concept:'Gradient Flow',             what:'Training requires gradients to reach every weight. Vanishing = learning stops. Residuals fix this.', why:'Architectural choices exist to enable gradient flow', color:'var(--orange)' },
    { concept:'Representation Learning',   what:'Each layer learns a new representation. Not hand-crafted features. End-to-end learning.', why:'Why DL replaced feature engineering', color:'var(--red)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Neuron anatomy */}
      <Panel title="SINGLE NEURON — HOW IT WORKS" accent={AC}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'center' }}>
          <svg width="100%" height="150" viewBox="0 0 500 150">
            {[0,1,2].map(i=>(
              <g key={i}>
                <circle cx={50} cy={28+i*48} r={14} fill="var(--gcyan)" stroke="var(--cyan)" strokeWidth={1.5}/>
                <text x={50} y={32+i*48} textAnchor="middle" fill="var(--cyan)" fontSize={10} fontFamily="JetBrains Mono">x{i+1}</text>
                <line x1={64} y1={28+i*48} x2={190} y2={76} stroke="var(--b2)" strokeWidth={1.5}/>
                <text x={118} y={22+i*38} fill="var(--gold)" fontSize={9} fontFamily="JetBrains Mono">w{i+1}</text>
              </g>
            ))}
            <circle cx={210} cy={76} r={22} fill="var(--raised)" stroke="var(--gold)" strokeWidth={2}/>
            <text x={210} y={73} textAnchor="middle" fill="var(--gold)" fontSize={11} fontFamily="JetBrains Mono">Σ</text>
            <text x={210} y={87} textAnchor="middle" fill="var(--t3)" fontSize={8} fontFamily="JetBrains Mono">+b</text>
            <line x1={232} y1={76} x2={288} y2={76} stroke="var(--t2)" strokeWidth={1.5}/>
            <text x={260} y={70} textAnchor="middle" fill="var(--t3)" fontSize={8} fontFamily="JetBrains Mono">z=Wx+b</text>
            <rect x={288} y={58} width={72} height={36} rx={4} fill="var(--raised)" stroke="var(--green)" strokeWidth={2}/>
            <text x={324} y={74} textAnchor="middle" fill="var(--green)" fontSize={11} fontFamily="JetBrains Mono">f(z)</text>
            <text x={324} y={86} textAnchor="middle" fill="var(--t3)" fontSize={8} fontFamily="JetBrains Mono">act.</text>
            <line x1={360} y1={76} x2={418} y2={76} stroke="var(--green)" strokeWidth={2}/>
            <circle cx={432} cy={76} r={13} fill="var(--ggreen)" stroke="var(--green)" strokeWidth={1.5}/>
            <text x={432} y={80} textAnchor="middle" fill="var(--green)" fontSize={10} fontFamily="JetBrains Mono">a</text>
          </svg>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            {[
              {sym:'xᵢ',    label:'Inputs',          desc:'Features from data or previous layer',           color:'var(--cyan)'},
              {sym:'wᵢ',    label:'Weights',          desc:'Learned params — control feature importance',    color:'var(--gold)'},
              {sym:'b',     label:'Bias',             desc:'Shifts activation. One per neuron.',             color:'var(--orange)'},
              {sym:'z=Wx+b',label:'Pre-activation',   desc:'Linear combination — still just a plane',       color:'var(--t2)'},
              {sym:'a=f(z)',label:'Activation',        desc:'Non-linearity applied. Enables complex shapes.', color:'var(--green)'},
            ].map(r=>(
              <div key={r.sym} style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                <code style={{ fontSize:'10px', color:r.color, background:r.color+'18', padding:'2px 8px', borderRadius:'2px', minWidth:'72px', textAlign:'center' }}>{r.sym}</code>
                <span style={{ fontSize:'10px', color:'var(--t1)', fontWeight:600, minWidth:'90px' }}>{r.label}</span>
                <span style={{ fontSize:'10px', color:'var(--t3)' }}>{r.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        {/* Timeline */}
        <Panel title="HISTORY — NEURAL NETWORKS TIMELINE" accent={AC}>
          <div>
            {timeline.map((t,i)=>(
              <div key={t.year} style={{ display:'flex', gap:'0', alignItems:'flex-start', paddingBottom:'9px', position:'relative', paddingLeft:'52px' }}>
                {i<timeline.length-1 && <div style={{ position:'absolute', left:'26px', top:'14px', bottom:0, width:'1px', background:'var(--b1)' }}/>}
                <div style={{ position:'absolute', left:'20px', top:'3px', width:'12px', height:'12px', borderRadius:'50%', background:t.color, border:'2px solid var(--deep)', zIndex:1 }}/>
                <div style={{ position:'absolute', left:0, top:'3px', fontSize:'9px', color:t.color, fontWeight:700, width:'17px', textAlign:'right' }}>{t.year.slice(2)}</div>
                <div style={{ fontSize:'10px', color:'var(--t2)', lineHeight:1.5 }}>{t.event}</div>
              </div>
            ))}
          </div>
        </Panel>

        {/* Param scale */}
        <Panel title="PARAMETER SCALE GROWTH" accent="var(--violet)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={paramScale} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
              <XAxis type="number" scale="log" domain={[1,1e12]}
                tickFormatter={v=>v<1e3?v:v<1e6?`${(v/1e3).toFixed(0)}K`:v<1e9?`${(v/1e6).toFixed(0)}M`:`${(v/1e9).toFixed(0)}B`}
                stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis type="category" dataKey="model" stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t2)'}} width={72}/>
              <Tooltip formatter={v=>v.toLocaleString()} contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
              <Bar dataKey="params" radius={[0,3,3,0]}>
                {paramScale.map((p,i)=><Cell key={i} fill={p.color}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <InfoBox color="var(--t3)" icon="◈">Model size ~10× every 2 years. Scaling laws: loss drops predictably with more params × data × compute.</InfoBox>
        </Panel>
      </div>

      {/* Concept table */}
      <Panel title="KEY CONCEPTS — NEURAL NETWORK FUNDAMENTALS" accent="var(--gold)">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
          {conceptTable.map(c=>(
            <div key={c.concept} style={{ padding:'11px 13px', background:'var(--raised)', border:`1px solid ${c.color}22`, borderRadius:'3px', borderLeft:`2px solid ${c.color}` }}>
              <div style={{ fontSize:'10px', color:c.color, fontWeight:700, marginBottom:'4px' }}>{c.concept}</div>
              <div style={{ fontSize:'10px', color:'var(--t1)', lineHeight:1.55, marginBottom:'4px' }}>{c.what}</div>
              <div style={{ fontSize:'9px', color:'var(--t3)' }}>→ {c.why}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: ARCHITECTURE
// Layer editor + init + network diagram + activation mini-selector + patterns
// ═══════════════════════════════════════════════════════════════
function TabArchitecture({ layers, setLayers, actName, setActName, initType, setInitType, weights, setWeights }) {
  const addLayer  = () => setLayers(l=>[...l.slice(0,-1),Math.max(1,l[l.length-2]||4),l[l.length-1]]);
  const rmLayer   = () => setLayers(l=>l.length>3?[...l.slice(0,-2),l[l.length-1]]:l);
  const updLayer  = (i,v) => setLayers(l=>{ const n=[...l]; n[i]=Math.max(1,Math.min(64,parseInt(v)||1)); return n; });
  const totalParams = layers.reduce((s,n,i)=>i===0?s:s+n*layers[i-1]+n,0);

  const doInit = () => {
    const {W,B}=initWeights(layers,initType);
    setWeights({W,B});
  };

  const archTypes = [
    {name:'Shallow Wide',  layers:[2,64,1],          desc:'1 wide hidden layer.',      color:'var(--gold)'},
    {name:'Deep Narrow',   layers:[2,8,8,8,1],        desc:'Hierarchical features.',    color:'var(--cyan)'},
    {name:'Bottleneck',    layers:[8,16,3,16,8],      desc:'Compression/latent space.', color:'var(--violet)'},
    {name:'Expanding',     layers:[2,4,8,16,1],       desc:'Low → high dim.',           color:'var(--green)'},
    {name:'Contracting',   layers:[16,8,4,2,1],       desc:'Classic classifier.',       color:'var(--orange)'},
  ];

  const wDist = weights?.W
    ? (()=>{
        const all=weights.W.flatMap(l=>l.flatMap(r=>r));
        const mn=Math.min(...all),mx=Math.max(...all),bins=22,bw=(mx-mn)/bins||1;
        return Array.from({length:bins},(_,i)=>({
          bin:+(mn+i*bw).toFixed(3),
          count:all.filter(v=>v>=mn+i*bw&&v<mn+(i+1)*bw).length,
        }));
      })()
    : [];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'350px 0.9fr', gap:'14px' }}>
      {/* Controls */}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        <Panel title="LAYER EDITOR" accent={AC}>
          {layers.map((n,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
              <div style={{ fontSize:'8px', color:'var(--t3)', width:'50px', textAlign:'right' }}>
                {i===0?'INPUT':i===layers.length-1?'OUTPUT':`HIDDEN ${i}`}
              </div>
              <input type="number" min={1} max={64} value={n} onChange={e=>updLayer(i,e.target.value)}
                disabled={i===0||i===layers.length-1}
                style={{ width:'48px', background:'var(--deep)', border:'1px solid var(--b1)', color:i===0||i===layers.length-1?'var(--t3)':'var(--t1)', padding:'4px 6px', fontSize:'11px', fontFamily:'JetBrains Mono', borderRadius:'2px', outline:'none' }}/>
              <div style={{ flex:1, height:'4px', background:'var(--b0)', borderRadius:'2px' }}>
                <div style={{ width:`${(n/64)*100}%`, height:'100%', background:i===0?'var(--cyan)':i===layers.length-1?'var(--green)':'var(--violet)', borderRadius:'2px' }}/>
              </div>
              <span style={{ fontSize:'9px', color:'var(--t3)', width:'16px' }}>{n}</span>
            </div>
          ))}
          <div style={{ display:'flex', gap:'6px' }}>
            <Btn onClick={addLayer} color="var(--green)" style={{flex:1,padding:'6px'}}>+ LAYER</Btn>
            <Btn onClick={rmLayer} color="var(--red)" disabled={layers.length<=3} style={{flex:1,padding:'6px'}}>- LAYER</Btn>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', marginTop:'8px', padding:'8px', background:'var(--deep)', borderRadius:'2px' }}>
            <div><div style={{fontSize:'8px',color:'var(--t3)'}}>DEPTH</div><div style={{color:'var(--cyan)',fontWeight:700,fontSize:'13px'}}>{layers.length} layers</div></div>
            <div><div style={{fontSize:'8px',color:'var(--t3)'}}>PARAMS</div><div style={{color:'var(--green)',fontWeight:700,fontSize:'13px'}}>{totalParams.toLocaleString()}</div></div>
          </div>
        </Panel>

        <Panel title="ACTIVATION" accent="var(--gold)">
          <Sel value={actName} onChange={setActName}
            options={Object.keys(ACTS).map(k=>({value:k,label:k.toUpperCase().replace('_',' ')}))}/>
          <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
            {Object.entries(ACTS).map(([k,v])=>(
              <div key={k} onClick={()=>setActName(k)} style={{
                padding:'4px 8px', border:`1px solid ${actName===k?v.color:'var(--b1)'}`,
                background:actName===k?v.color+'18':'transparent',
                color:actName===k?v.color:'var(--t3)',
                fontSize:'9px', cursor:'pointer', borderRadius:'2px', fontFamily:'JetBrains Mono',
              }}>{k}</div>
            ))}
          </div>
        </Panel>

        <Panel title="WEIGHT INIT" accent="var(--violet)">
          <Sel label="STRATEGY" value={initType} onChange={setInitType}
            options={[
              {value:'xavier', label:'Xavier/Glorot (tanh/sigmoid)'},
              {value:'he',     label:'He/Kaiming (ReLU)'},
              {value:'lecun',  label:'LeCun (SELU)'},
              {value:'random', label:'Random Normal σ=0.5'},
              {value:'zeros',  label:'Zeros (symmetry break — bad)'},
            ]}/>
          <Btn onClick={doInit} color="var(--violet)" style={{width:'100%'}}>INITIALIZE WEIGHTS</Btn>
          {weights && (
            <div style={{marginTop:'8px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>WEIGHT DISTRIBUTION</div>
              <ResponsiveContainer width="100%" height={70}>
                <BarChart data={wDist}>
                  <XAxis dataKey="bin" hide/><YAxis hide/>
                  <Bar dataKey="count" fill="var(--violet)" radius={[1,1,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      {/* Network diagram */}
      <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
        <Panel title="NETWORK DIAGRAM — LIVE TOPOLOGY" accent={AC}>
          <NetworkDiagram layers={layers} activations={null}/>
          <div style={{ display:'flex', gap:'10px', marginTop:'6px', padding:'6px 8px', background:'var(--deep)', borderRadius:'2px', fontSize:'9px', color:'var(--t3)' }}>
            <span style={{color:'var(--cyan)'}}>⬤ Input layer</span>
            <span style={{color:'var(--violet)'}}>⬤ Hidden layers</span>
            <span style={{color:'var(--green)'}}>⬤ Output layer</span>
            <span style={{marginLeft:'auto'}}>Nodes = neurons. Edges = weights. Activate layers in FORWARD/TRAIN tabs to see values.</span>
          </div>
        </Panel>

        {/* Architecture patterns */}
        <Panel title="PRESET ARCHITECTURE PATTERNS" accent="var(--violet)">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'8px' }}>
            {archTypes.map(a=>(
              <div key={a.name} onClick={()=>setLayers(a.layers)}
                style={{ padding:'11px', background:'var(--raised)', border:`1px solid ${a.color}30`, borderRadius:'3px', cursor:'pointer', transition:'all .15s' }}
                onMouseEnter={e=>e.currentTarget.style.borderColor=a.color}
                onMouseLeave={e=>e.currentTarget.style.borderColor=a.color+'30'}>
                <div style={{fontSize:'10px',color:a.color,fontWeight:700,marginBottom:'4px'}}>{a.name}</div>
                <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'5px'}}>[{a.layers.join('→')}]</div>
                <div style={{fontSize:'9px',color:'var(--t2)',lineHeight:1.5}}>{a.desc}</div>
                <div style={{marginTop:'5px',fontSize:'8px',color:a.color}}>→ click to load</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: FORWARD PASS
// Left: live network diagram (activations glow). Right: controls + layer breakdown
// ═══════════════════════════════════════════════════════════════
function TabForward({ layers, weights, setWeights, actName, setActName, log }) {
  const [input, setInput] = useState(()=>Array(layers[0]).fill(0).map((_,i)=>i===0?1:0));
  const [fwdResult, setFwdResult] = useState(null);

  const run = useCallback(() => {
    if(!weights){ log('Initialize weights first (Architecture tab)','err'); return; }
    try {
      const inp = input.slice(0, layers[0]);
      // Pad if needed
      while(inp.length < layers[0]) inp.push(0);
      const res = forward(inp, weights.W, weights.B, actName, 'sigmoid');
      setFwdResult(res);
      const pred = res.as[res.as.length-1];
      log(`Forward pass ✓  output=[${pred.map(v=>v.toFixed(4)).join(', ')}]`,'ok');
    } catch(e){ log(`Error: ${e.message}`,'err'); }
  }, [input, weights, actName, layers]);

  // Auto-run on input change
  useEffect(()=>{ if(weights) run(); },[input, weights]);

  const setInp = (i,v) => setInput(prev=>{ const n=[...prev]; n[i]=parseFloat(v)||0; return n; });

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:'14px' }}>
      {/* LEFT: Live network diagram */}
      <Panel title="NETWORK — LIVE ACTIVATIONS" accent={AC}>
        <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'8px'}}>
          Each neuron shows its activation value. Brightness = magnitude. Red = negative. Blue = positive.
          Dead neurons (ReLU=0) shown dark.
        </div>
        <NetworkDiagram layers={layers} activations={fwdResult?.as ?? null}/>
        {fwdResult && (
          <div style={{ marginTop:'8px', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))', gap:'6px' }}>
            {fwdResult.as.map((a,li)=>{
              const dead = a.filter(v=>v===0).length;
              const max  = Math.max(...a.map(Math.abs),0.001);
              return (
                <div key={li} style={{padding:'7px 10px',background:'var(--deep)',borderRadius:'2px',textAlign:'center'}}>
                  <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'2px'}}>{li===0?'INPUT':li===fwdResult.as.length-1?'OUTPUT':`HIDDEN ${li}`}</div>
                  <div style={{fontSize:'11px',color:li===0?'var(--cyan)':li===fwdResult.as.length-1?'var(--green)':'var(--violet)',fontWeight:700}}>{a.length} neurons</div>
                  {li>0 && <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'2px'}}>max act: {max.toFixed(2)}</div>}
                  {li>0 && dead>0 && <div style={{fontSize:'8px',color:'var(--red)'}}>{dead} dead (0)</div>}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* RIGHT: Controls + layer details */}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        <Panel title="INPUT" accent={AC}>
          {Array.from({length:Math.min(layers[0],8)},(_,i)=>(
            <div key={i} style={{marginBottom:'10px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                <span style={{fontSize:'10px',color:'var(--cyan)'}}>x{i+1}</span>
                <input type="number" step={0.1} value={input[i]??0} onChange={e=>setInp(i,e.target.value)}
                  style={{width:'64px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'3px 6px',fontSize:'10px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none',textAlign:'right'}}/>
              </div>
              <input type="range" min={-2} max={2} step={0.05} value={Math.max(-2,Math.min(2,input[i]??0))}
                onChange={e=>setInp(i,e.target.value)} style={{width:'100%'}}/>
            </div>
          ))}
          {layers[0]>8 && <div style={{fontSize:'9px',color:'var(--t3)'}}>… {layers[0]-8} more inputs</div>}
          <Sel label="ACTIVATION" value={actName} onChange={setActName}
            options={Object.keys(ACTS).map(k=>({value:k,label:k.toUpperCase().replace('_',' ')}))}/>
        </Panel>

        {fwdResult && (
          <>
            <Panel title="OUTPUT" accent="var(--green)">
              {fwdResult.as[fwdResult.as.length-1].map((v,i)=>(
                <div key={i} style={{textAlign:'center',padding:'10px',background:'var(--deep)',borderRadius:'2px',marginBottom:'4px'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>ŷ{i+1}</div>
                  <div style={{fontSize:'26px',fontFamily:'Syne',fontWeight:800,color:v>0.5?'var(--green)':'var(--red)'}}>{v.toFixed(5)}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>class {v>0.5?1:0} · {(v*100).toFixed(1)}% confidence</div>
                </div>
              ))}
            </Panel>

            <Panel title="PRE vs POST ACTIVATION — LAYER 1" accent="var(--gold)">
              {fwdResult.zs[0] && (
                <ResponsiveContainer width="100%" height={110}>
                  <BarChart data={fwdResult.zs[0].slice(0,12).map((z,i)=>({n:i+1,z:+z.toFixed(3),a:+fwdResult.as[1][i].toFixed(3)}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="n" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
                    <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}}/>
                    <Bar dataKey="z" fill="var(--gold)" opacity={0.7} name="z (pre)"/>
                    <Bar dataKey="a" fill="var(--cyan)" opacity={0.9} name="a (post)"/>
                    <Legend/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: TRAIN
// Left: network live during training (updates per epoch batch). Right: config + loss + decision boundary
// ═══════════════════════════════════════════════════════════════
function TabTrain({ layers, weights, setWeights, actName, log }) {
  const [dataset,   setDataset]   = useState('xor');
  const [lr,        setLr]        = useState(0.05);
  const [epochs,    setEpochs]    = useState(1000);
  const [running,   setRunning]   = useState(false);
  const [trainResult, setTrainResult] = useState(null);
  const [liveActs,  setLiveActs]  = useState(null); // activations mid-training
  const [boundary,  setBoundary]  = useState(null);
  const [progress,  setProgress]  = useState(0);

  const ds = DATASETS[dataset];

  const run = async () => {
    setRunning(true); setTrainResult(null); setBoundary(null); setLiveActs(null); setProgress(0);
    log(`Training [${layers.join('→')}] on ${ds.name} | lr=${lr} | epochs=${epochs} | act=${actName}`);

    let {W,B} = initWeights(layers,'he');
    const history=[];
    const step=Math.max(1,Math.floor(epochs/100));
    const snapStep=Math.max(1,Math.floor(epochs/10)); // update live diagram 10×

    for(let ep=0;ep<epochs;ep++){
      let totalLoss=0;
      ds.inputs.forEach((x,i)=>{
        const res=backprop(x,ds.targets[i],W,B,actName,lr);
        W=res.W; B=res.B; totalLoss+=res.loss;
      });
      if(ep%step===0) history.push({epoch:ep,loss:+(totalLoss/ds.inputs.length).toFixed(6)});
      if(ep%snapStep===0){
        // snapshot: run forward on first sample to show live activations
        try{
          const snap=forward(ds.inputs[0],W,B,actName,'sigmoid');
          setLiveActs(snap.as);
          setProgress(Math.round(ep/epochs*100));
          await new Promise(r=>setTimeout(r,0)); // yield to renderer
        } catch(e){}
      }
    }

    const preds=ds.inputs.map((x,i)=>{
      const {as}=forward(x,W,B,actName,'sigmoid');
      const p=as[as.length-1][0];
      return {input:x.join(','),target:ds.targets[i][0],pred:+p.toFixed(4),correct:Math.round(p)===ds.targets[i][0]};
    });
    const acc=preds.filter(p=>p.correct).length/preds.length;

    if(layers[0]===2) setBoundary(decisionGrid(W,B,actName,24));
    setWeights({W,B});
    setTrainResult({history,preds,acc,finalLoss:history[history.length-1]?.loss});
    log(`✓ Done. Accuracy: ${(acc*100).toFixed(1)}% | Final loss: ${history[history.length-1]?.loss}`,'ok');
    setRunning(false); setProgress(0);
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:'14px' }}>
      {/* LEFT: Live network + decision boundary */}
      <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
        <Panel title={running?`TRAINING IN PROGRESS — ${progress}%`:'NETWORK — TRAINING ACTIVATIONS'} accent={running?'var(--gold)':AC}>
          <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'6px'}}>
            {running
              ? `Epoch ${Math.round(progress*epochs/100)} / ${epochs} — Watching sample [${ds.inputs[0].join(',')}] flow through the network in real-time`
              : trainResult ? 'Final trained weights — hover neurons to inspect values' : 'Configure and click TRAIN to watch the network learn live'}
          </div>
          <NetworkDiagram layers={layers} activations={liveActs}/>
          {running && (
            <div style={{marginTop:'8px',height:'4px',background:'var(--b0)',borderRadius:'2px'}}>
              <div style={{width:`${progress}%`,height:'100%',background:'var(--gold)',borderRadius:'2px',transition:'width .3s'}}/>
            </div>
          )}
        </Panel>

        {/* Decision boundary */}
        {boundary && layers[0]===2 && (
          <Panel title="DECISION BOUNDARY" accent="var(--violet)">
            <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'6px'}}>Cyan = class 1, dark = class 0. Circles = training samples (green=1, red=0).</div>
            <div style={{position:'relative',display:'inline-block'}}>
              <div style={{display:'flex',flexWrap:'wrap',width:'260px',height:'260px'}}>
                {boundary.map((cell,i)=>(
                  <div key={i} style={{
                    width:`${100/24}%`,height:`${100/24}%`,
                    background:`rgba(${cell.p>0.5?'0,217,255':'176,138,255'},${0.08+cell.p*0.7})`,
                  }}/>
                ))}
              </div>
              <div style={{position:'absolute',top:0,left:0,width:'260px',height:'260px'}}>
                {ds.inputs.map((inp,i)=>{
                  const px=((inp[0]+1.2)/2.4)*260, py=((inp[1]+1.2)/2.4)*260;
                  return <div key={i} style={{position:'absolute',left:px-6,top:py-6,width:12,height:12,borderRadius:'50%',background:ds.targets[i][0]===1?'var(--green)':'var(--red)',border:'2px solid var(--void)'}}/>;
                })}
              </div>
            </div>
          </Panel>
        )}
      </div>

      {/* RIGHT: Config + results */}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        <Panel title="TRAIN CONFIG" accent={AC}>
          <Sel label="DATASET" value={dataset} onChange={setDataset}
            options={Object.entries(DATASETS).map(([k,v])=>({value:k,label:v.name}))}/>
          <InfoBox color="var(--t3)" icon="◈">{DATASETS[dataset].desc}</InfoBox>
          <div style={{marginTop:'10px'}}>
            <Sl label="Learning Rate" value={lr} min={0.001} max={0.3} step={0.001} onChange={setLr} color={AC}/>
            <Sl label="Epochs" value={epochs} min={100} max={5000} step={100} onChange={setEpochs} unit="" color="var(--gold)"/>
          </div>
          <Btn onClick={run} disabled={running} color={AC} style={{width:'100%'}}>
            {running?`◌ TRAINING ${progress}%`:'▶ TRAIN NETWORK'}
          </Btn>
        </Panel>

        {trainResult && (
          <>
            <Panel title="LOSS CURVE" accent="var(--green)">
              <ResponsiveContainer width="100%" height={130}>
                <AreaChart data={trainResult.history}>
                  <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
                  <XAxis dataKey="epoch" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
                  <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}}/>
                  <Area type="monotone" dataKey="loss" stroke={AC} fill={AC+'20'} dot={false} strokeWidth={2}/>
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="PREDICTIONS" accent="var(--violet)">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'8px'}}>
                <div style={{padding:'8px',background:'var(--deep)',borderRadius:'2px',textAlign:'center'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>ACCURACY</div>
                  <div style={{fontSize:'22px',fontFamily:'Syne',fontWeight:800,color:trainResult.acc===1?'var(--green)':trainResult.acc>0.75?'var(--gold)':'var(--red)'}}>{(trainResult.acc*100).toFixed(0)}%</div>
                </div>
                <div style={{padding:'8px',background:'var(--deep)',borderRadius:'2px',textAlign:'center'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>FINAL LOSS</div>
                  <div style={{fontSize:'16px',fontFamily:'Syne',fontWeight:800,color:'var(--cyan)'}}>{trainResult.finalLoss}</div>
                </div>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px',fontFamily:'JetBrains Mono'}}>
                <thead><tr>{['In','Target','Pred','✓'].map(h=><th key={h} style={{padding:'3px 6px',borderBottom:'1px solid var(--b1)',color:'var(--t3)',textAlign:'left'}}>{h}</th>)}</tr></thead>
                <tbody>
                  {trainResult.preds.map((p,i)=>(
                    <tr key={i} style={{background:i%2===0?'var(--deep)':'transparent'}}>
                      <td style={{padding:'3px 6px',color:'var(--t3)'}}>[{p.input}]</td>
                      <td style={{padding:'3px 6px',color:'var(--cyan)'}}>{p.target}</td>
                      <td style={{padding:'3px 6px',color:'var(--t2)'}}>{p.pred}</td>
                      <td style={{padding:'3px 6px',color:p.correct?'var(--green)':'var(--red)'}}>{p.correct?'✓':'✗'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: PLAYGROUND
// Left: live network (updates instantly as sliders move). Right: sliders
// ═══════════════════════════════════════════════════════════════
function TabPlayground({ layers, weights, actName, log }) {
  const [inputs, setInputs] = useState(()=>Array(layers[0]).fill(0));
  const [result, setResult] = useState(null);

  useEffect(()=>{
    if(!weights) return;
    try {
      const inp=inputs.slice(0,layers[0]);
      while(inp.length<layers[0]) inp.push(0);
      const res=forward(inp,weights.W,weights.B,actName,'sigmoid');
      setResult(res);
    } catch(e){}
  },[inputs,weights,actName]);

  const setInp=(i,v)=>setInputs(prev=>{ const n=[...prev]; n[i]=parseFloat(v)||0; return n; });

  if(!weights) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'300px',border:'1px dashed var(--b1)',borderRadius:'4px',flexDirection:'column',gap:'8px',color:'var(--t3)'}}>
      <div style={{fontSize:'28px',opacity:.2}}>◐</div>
      <div>Train a network first (TRAIN tab), then explore here</div>
    </div>
  );

  const output = result?.as[result.as.length-1] ?? [];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:'14px' }}>
      {/* LEFT: Live network — updates every slider move */}
      <Panel title="NETWORK — REAL-TIME INFERENCE" accent={AC}>
        <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'8px'}}>
          Move sliders → see activations update instantly through every layer. Watch how information flows and transforms.
        </div>
        <NetworkDiagram layers={layers} activations={result?.as ?? null}/>

        {result && (
          <div style={{marginTop:'10px',display:'flex',flexDirection:'column',gap:'6px'}}>
            {result.as.slice(1).map((a,li)=>{
              const maxV=Math.max(...a.map(Math.abs),0.001);
              return (
                <div key={li} style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  <span style={{fontSize:'9px',color:'var(--t3)',width:'60px'}}>{li===result.as.length-2?'OUTPUT':`H${li+1}`}</span>
                  <div style={{flex:1,display:'flex',gap:'2px'}}>
                    {a.slice(0,16).map((v,ni)=>(
                      <div key={ni} title={`${v.toFixed(4)}`} style={{
                        height:'20px',flex:1,maxWidth:'22px',borderRadius:'2px',
                        background:v>0?`var(--cyan)${Math.round(20+Math.abs(v)/maxV*220).toString(16).padStart(2,'0')}`:v<0?`var(--red)${Math.round(20+Math.abs(v)/maxV*180).toString(16).padStart(2,'0')}`:'var(--b1)',
                        border:`1px solid ${v===0?'var(--b0)':'var(--b1)'}`,
                      }}/>
                    ))}
                    {a.length>16 && <span style={{fontSize:'8px',color:'var(--t3)',alignSelf:'center'}}>+{a.length-16}</span>}
                  </div>
                  <span style={{fontSize:'9px',color:'var(--t3)',width:'38px',textAlign:'right'}}>max:{maxV.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* RIGHT: Sliders + output */}
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        <Panel title="INPUT SLIDERS" accent={AC}>
          {inputs.slice(0,layers[0]).map((v,i)=>(
            <div key={i} style={{marginBottom:'12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                <span style={{fontSize:'10px',color:'var(--cyan)'}}>x{i+1}</span>
                <span style={{fontSize:'10px',color:'var(--t1)',fontWeight:700}}>{v.toFixed(2)}</span>
              </div>
              <input type="range" min={-2} max={2} step={0.05} value={v}
                onChange={e=>setInp(i,e.target.value)} style={{width:'100%'}}/>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'8px',color:'var(--t3)'}}>
                <span>-2</span><span>0</span><span>+2</span>
              </div>
            </div>
          ))}
        </Panel>

        <Panel title="OUTPUT" accent="var(--green)">
          {output.map((v,i)=>(
            <div key={i} style={{marginBottom:'8px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}>
                <span style={{fontSize:'10px',color:'var(--t3)'}}>ŷ{i+1}</span>
                <span style={{fontSize:'10px',color:v>0.5?'var(--green)':'var(--red)',fontWeight:700}}>class {v>0.5?1:0}</span>
              </div>
              <div style={{height:'28px',background:'var(--b0)',borderRadius:'3px',overflow:'hidden',position:'relative'}}>
                <div style={{height:'100%',width:`${v*100}%`,background:`linear-gradient(90deg,var(--cyan)44,${v>0.5?'var(--green)':'var(--red)'})`,transition:'width .08s'}}/>
                <span style={{position:'absolute',right:'8px',top:'50%',transform:'translateY(-50%)',fontSize:'11px',color:'var(--t1)',fontWeight:700}}>{v.toFixed(4)}</span>
              </div>
            </div>
          ))}
          {output.length>0 && (
            <div style={{marginTop:'8px',padding:'8px',background:'var(--deep)',borderRadius:'2px',textAlign:'center',fontSize:'11px',color:output[0]>0.5?'var(--green)':'var(--red)',fontWeight:700}}>
              → {output[0]>0.5?'CLASS 1':'CLASS 0'} ({(Math.max(output[0],1-output[0])*100).toFixed(1)}% confidence)
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: COMPARE
// ═══════════════════════════════════════════════════════════════
function TabCompare({ log }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const configs = [
    {label:'No Hidden Layer', layers:[2,1],         act:'sigmoid', color:P[4]},
    {label:'Shallow [4]',     layers:[2,4,1],        act:'relu',    color:P[0]},
    {label:'Medium [8×2]',    layers:[2,8,8,1],      act:'relu',    color:P[1]},
    {label:'Deep [8×3]',      layers:[2,8,8,8,1],    act:'relu',    color:P[2]},
    {label:'Wide [32]',       layers:[2,32,1],        act:'relu',    color:P[3]},
    {label:'Tanh [8×2]',      layers:[2,8,8,1],      act:'tanh',    color:P[5]},
  ];

  const runAll = async () => {
    setRunning(true);
    log('Comparing 6 architectures on XOR...');
    await new Promise(r=>setTimeout(r,20));
    const ep=1000, ds=DATASETS.xor;
    const allR = configs.map(cfg=>{
      let {W,B}=initWeights(cfg.layers,'he');
      const hist=[];
      const step=Math.max(1,Math.floor(ep/80));
      for(let e=0;e<ep;e++){
        let tl=0;
        ds.inputs.forEach((x,i)=>{ const r=backprop(x,ds.targets[i],W,B,cfg.act,0.05); W=r.W;B=r.B;tl+=r.loss; });
        if(e%step===0) hist.push({epoch:e,loss:+(tl/ds.inputs.length).toFixed(6)});
      }
      const preds=ds.inputs.map((x,i)=>{ const {as}=forward(x,W,B,cfg.act,'sigmoid'); return Math.round(as[as.length-1][0])===ds.targets[i][0]; });
      const acc=preds.filter(Boolean).length/preds.length;
      const params=cfg.layers.reduce((s,n,i)=>i===0?s:s+n*cfg.layers[i-1]+n,0);
      return {...cfg,hist,acc,finalLoss:hist[hist.length-1]?.loss,params};
    });
    setResults(allR);
    log(`✓ Done. Best: ${[...allR].sort((a,b)=>b.acc-a.acc)[0].label} (${([...allR].sort((a,b)=>b.acc-a.acc)[0].acc*100).toFixed(0)}% acc)`,'ok');
    setRunning(false);
  };

  const merged = results
    ? results[0].hist.map((d,i)=>({epoch:d.epoch,...Object.fromEntries(results.map(r=>[r.label,r.hist[i]?.loss]))}))
    : [];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <InfoBox color={AC} icon="◈">6 architectures, same dataset (XOR), same LR=0.05, 1000 epochs. Tests how depth, width, and activation affect learning.</InfoBox>
        <Btn onClick={runAll} disabled={running} color={AC} style={{marginLeft:'14px',flexShrink:0}}>
          {running?'◌ RUNNING...':'▶ RUN COMPARISON'}
        </Btn>
      </div>

      {results ? (
        <>
          <Panel title="LEADERBOARD" accent={AC}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px',fontFamily:'JetBrains Mono'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Rank','Architecture','Activation','Params','Final Loss','Accuracy'].map(h=>(
                  <th key={h} style={{padding:'6px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b1)',letterSpacing:'.08em'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {[...results].sort((a,b)=>b.acc-a.acc).map((r,i)=>(
                  <tr key={r.label} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.06)'}}>
                    <td style={{padding:'6px 10px',color:i===0?'var(--gold)':'var(--t3)',fontWeight:i===0?700:400}}>{i===0?'🥇':i+1}</td>
                    <td style={{padding:'6px 10px',color:r.color,fontWeight:600}}>{r.label}</td>
                    <td style={{padding:'6px 10px',color:'var(--t2)'}}>{r.act}</td>
                    <td style={{padding:'6px 10px',color:'var(--t3)'}}>{r.params}</td>
                    <td style={{padding:'6px 10px',color:'var(--cyan)'}}>{r.finalLoss}</td>
                    <td style={{padding:'6px 10px',color:r.acc===1?'var(--green)':r.acc>0.75?'var(--gold)':'var(--red)',fontWeight:700}}>{(r.acc*100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
            <Panel title="LOSS CONVERGENCE" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={merged}>
                  <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
                  <XAxis dataKey="epoch" stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}}/>
                  <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
                  {results.map(r=><Line key={r.label} type="monotone" dataKey={r.label} stroke={r.color} dot={false} strokeWidth={2}/>)}
                  <Legend/>
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="ACCURACY + PARAMS" accent="var(--green)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={results.map(r=>({name:r.label.split(' ').slice(0,2).join(' '),acc:+(r.acc*100).toFixed(0),params:r.params,color:r.color}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="name" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
                  <YAxis yAxisId="acc" stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,100]} tickFormatter={v=>`${v}%`}/>
                  <Tooltip formatter={(v,n)=>n==='acc'?`${v}%`:v.toLocaleString()} contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
                  <Bar yAxisId="acc" dataKey="acc" radius={[3,3,0,0]}>
                    {results.map((r,i)=><Cell key={i} fill={r.color}/>)}
                  </Bar>
                  <Legend/>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        </>
      ) : (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',border:'1px dashed var(--b1)',borderRadius:'4px',minHeight:'280px',flexDirection:'column',gap:'8px',color:'var(--t3)'}}>
          <div style={{fontSize:'28px',opacity:.2}}>⬡</div>
          <div>Click RUN COMPARISON to benchmark 6 architectures</div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════
export default function NeuralNetworks({ mode }) {
  const [tab,      setTab]      = useState('overview');
  const [logs,     setLogs]     = useState([{ts:'00:00:00',msg:'Neural Networks — explore the tabs above.',t:'info'}]);
  const [layers,   setLayers]   = useState([2,4,4,1]);
  const [actName,  setActName]  = useState('relu');
  const [initType, setInitType] = useState('xavier');
  const [weights,  setWeights]  = useState(null);

  const log = (msg,t='info') => setLogs(l=>[...l.slice(-100),{ts:timestamp(),msg,t}]);

  const TAB_META = {
    overview:     {label:'OVERVIEW',      color:'var(--cyan)'},
    architecture: {label:'ARCHITECTURE',  color:'var(--violet)'},
    forward:      {label:'FORWARD PASS',  color:'var(--green)'},
    train:        {label:'TRAIN',         color:'var(--cyan)'},
    playground:   {label:'PLAYGROUND',    color:'var(--orange)'},
    compare:      {label:'COMPARE',       color:'var(--violet)'},
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Tabs */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'6px 14px',
            border:`1px solid ${tab===t?TAB_META[t].color:'transparent'}`,
            background:tab===t?TAB_META[t].color+'20':'transparent',
            color:tab===t?TAB_META[t].color:'var(--t3)',
            fontSize:'10px',cursor:'pointer',borderRadius:'2px',
            fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400,
          }}>{TAB_META[t].label}</button>
        ))}
      </div>

      {/* Content */}
      <div>
        {tab==='overview'     && <TabOverview/>}
        {tab==='architecture' && <TabArchitecture layers={layers} setLayers={setLayers} actName={actName} setActName={setActName} initType={initType} setInitType={setInitType} weights={weights} setWeights={setWeights}/>}
        {tab==='forward'      && <TabForward layers={layers} weights={weights} setWeights={setWeights} actName={actName} setActName={setActName} log={log}/>}
        {tab==='train'        && <TabTrain layers={layers} weights={weights} setWeights={setWeights} actName={actName} log={log}/>}
        {tab==='playground'   && <TabPlayground layers={layers} weights={weights} actName={actName} log={log}/>}
        {tab==='compare'      && <TabCompare log={log}/>}
      </div>

      <LogPanel logs={logs} height={75}/>
    </div>
  );
}
